import { exportToCanvas } from "@excalidraw/utils/export";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import JSZip from "jszip";

import {
  DEFAULT_EXPORT_PADDING,
  EXPORT_IMAGE_TYPES,
  isFirefox,
  EXPORT_SCALES,
  cloneJSON,
  SVG_DOCUMENT_PREAMBLE,
} from "@excalidraw/common";

import { getNonDeletedElements } from "@excalidraw/element";

import type { NonDeletedExcalidrawElement } from "@excalidraw/element/types";

import {
  actionChangeExportBackground,
  actionChangeExportEmbedScene,
  actionChangeExportScale,
  actionChangeProjectName,
} from "../actions/actionExport";
import { probablySupportsClipboardBlob } from "../clipboard";
import { prepareElementsForExport } from "../data";
import { canvasToBlob } from "../data/blob";
import { serializeAsJSON } from "../data/json";
import { nativeFileSystemSupported, fileSave } from "../data/filesystem";
import { saveAsPdf } from "../data/pdf";
import { exportToSvg } from "../scene/export";
import { useCopyStatus } from "../hooks/useCopiedIndicator";

import { t } from "../i18n";
import { isSomeElementSelected } from "../scene";

import { copyIcon, downloadIcon, helpIcon } from "./icons";
import { Dialog } from "./Dialog";
import { RadioGroup } from "./RadioGroup";
import { Switch } from "./Switch";
import { Tooltip } from "./Tooltip";
import { FilledButton } from "./FilledButton";

import "./ImageExportDialog.scss";

import type { ActionManager } from "../actions/manager";

import type {
  AppClassProperties,
  BinaryFiles,
  UIAppState,
  AppState,
} from "../types";

export const ErrorCanvasPreview = () => {
  return (
    <div>
      <h3>{t("canvasError.cannotShowPreview")}</h3>
      <p>
        <span>{t("canvasError.canvasTooBig")}</span>
      </p>
      <em>({t("canvasError.canvasTooBigTip")})</em>
    </div>
  );
};

type ExportAsModalProps = {
  appStateSnapshot: Readonly<UIAppState>;
  elementsSnapshot: readonly NonDeletedExcalidrawElement[];
  files: BinaryFiles;
  actionManager: ActionManager;
  onExportImage: AppClassProperties["onExportImage"];
  name: string;
  exportWithDarkMode: boolean;
  onCloseRequest: () => void;
  setAppState: React.Component<any, AppState>["setState"];
};

const ExportAsModal = ({
  appStateSnapshot,
  elementsSnapshot,
  files,
  actionManager,
  onExportImage,
  name,
  exportWithDarkMode,
  onCloseRequest,
  setAppState,
}: ExportAsModalProps) => {
  const selectedPapers = useMemo(
    () =>
      elementsSnapshot.filter(
        (el) =>
          appStateSnapshot.selectedElementIds[el.id] &&
          el.customData?.isPapel &&
          !el.isDeleted,
      ),
    [appStateSnapshot, elementsSnapshot],
  );

  const paperElements =
    selectedPapers.length > 0
      ? elementsSnapshot.filter((el) => {
          if (el.isDeleted) {
            return false;
          }
          if (selectedPapers.some((p) => p.id === el.id)) {
            return true;
          }
          if (el.groupIds && el.groupIds.length > 0) {
            return selectedPapers.some(
              (p) =>
                p.groupIds &&
                p.groupIds.length > 0 &&
                el.groupIds.some((id) => p.groupIds!.includes(id)),
            );
          }
          return false;
        })
      : elementsSnapshot;

  const hasSelection = isSomeElementSelected(
    getNonDeletedElements(elementsSnapshot),
    appStateSnapshot,
  );

  const [projectName, setProjectName] = useState(name);
  const [exportSelectionOnly] = useState(hasSelection);
  const [exportWithBackground, setExportWithBackground] = useState(true);
  const [embedScene, setEmbedScene] = useState(true);
  const [exportScale, setExportScale] = useState(appStateSnapshot.exportScale);
  const [exportWithBorder, setExportWithBorder] = useState(true);

  const previewRef = useRef<HTMLDivElement>(null);
  const previewRenderRequestIdRef = useRef(0);
  const [renderError, setRenderError] = useState<Error | null>(null);

  const { onCopy, copyStatus, resetCopyStatus } = useCopyStatus();

  useEffect(() => {
    // if user changes setting right after export to clipboard, reset the status
    // so they don't have to wait for the timeout to click the button again
    resetCopyStatus();
  }, [
    projectName,
    exportWithBackground,
    exportWithBorder,
    exportScale,
    embedScene,
    resetCopyStatus,
  ]);

  const { exportedElements, exportingFrame } = prepareElementsForExport(
    paperElements.map((el) => {
      if (el.customData?.isPapel) {
        return {
          ...el,
          backgroundColor: exportWithBackground
            ? el.backgroundColor
            : "transparent",
          strokeColor: exportWithBorder ? el.strokeColor : "transparent",
        };
      }
      return el;
    }),
    appStateSnapshot,
    exportSelectionOnly,
  );
  const exportImageOptions = {
    exportingFrame,
    exportBackground: exportWithBackground,
    viewBackgroundColor: appStateSnapshot.viewBackgroundColor,
  };

  const getExportedElementsForPaper = useCallback(
    (paperId: string) => {
      return exportedElements.filter((el) => {
        if (el.id === paperId) {
          return true;
        }
        if (el.groupIds && el.groupIds.length > 0) {
          const paper = selectedPapers.find((p) => p.id === paperId);
          if (paper && paper.groupIds && paper.groupIds.length > 0) {
            return el.groupIds.some((id) => paper.groupIds!.includes(id));
          }
        }
        return false;
      });
    },
    [exportedElements, selectedPapers],
  );

  useEffect(() => {
    const previewNode = previewRef.current;
    if (!previewNode) {
      return;
    }
    const maxWidth = previewNode.offsetWidth;
    const maxHeight = previewNode.offsetHeight;
    if (!maxWidth) {
      return;
    }

    const requestId = ++previewRenderRequestIdRef.current;
    const isStaleRequest = () => {
      return requestId !== previewRenderRequestIdRef.current;
    };

    const renderPreviews = async () => {
      try {
        const canvases: HTMLCanvasElement[] = [];

        if (selectedPapers.length > 1) {
          for (let i = 0; i < selectedPapers.length; i++) {
            const paper = selectedPapers[i];
            const elementsForThisPaper = getExportedElementsForPaper(paper.id);
            const canvas = await exportToCanvas({
              elements: elementsForThisPaper,
              appState: {
                ...appStateSnapshot,
                name: projectName,
                exportBackground: exportWithBackground,
                exportWithDarkMode,
                exportScale,
                exportEmbedScene: embedScene,
              },
              files,
              exportPadding: DEFAULT_EXPORT_PADDING,
              maxWidthOrHeight: Math.max(maxWidth, maxHeight),
              exportingFrame: paper as any,
            });
            await canvasToBlob(canvas); // Trigger potential error early
            canvases.push(canvas);
          }
        } else {
          const canvas = await exportToCanvas({
            elements: exportedElements,
            appState: {
              ...appStateSnapshot,
              name: projectName,
              exportBackground: exportWithBackground,
              exportWithDarkMode,
              exportScale,
              exportEmbedScene: embedScene,
            },
            files,
            exportPadding: DEFAULT_EXPORT_PADDING,
            maxWidthOrHeight: Math.max(maxWidth, maxHeight),
            exportingFrame,
          });
          await canvasToBlob(canvas);
          canvases.push(canvas);
        }

        if (isStaleRequest()) {
          return;
        }

        setRenderError(null);
        previewNode.replaceChildren(...canvases);
      } catch (error: any) {
        if (isStaleRequest()) {
          return;
        }
        console.error(error);
        if (error.name === "CANVAS_POSSIBLY_TOO_BIG") {
          setRenderError(new Error(t("canvasError.canvasTooBig")));
        } else {
          setRenderError(error);
        }
      }
    };

    renderPreviews();

    return () => {
      previewRenderRequestIdRef.current += 1;
    };
  }, [
    appStateSnapshot,
    files,
    exportedElements,
    exportingFrame,
    getExportedElementsForPaper,
    projectName,
    exportWithBackground,
    exportWithDarkMode,
    exportScale,
    embedScene,
    selectedPapers,
  ]);

  return (
    <div className="ImageExportModal">
      <h3>Exportar como</h3>
      <div className="ImageExportModal__preview">
        <div className="ImageExportModal__preview__canvas" ref={previewRef}>
          {renderError && <ErrorCanvasPreview />}
        </div>
        <div className="ImageExportModal__preview__filename">
          {!nativeFileSystemSupported && (
            <input
              type="text"
              className="TextInput"
              value={projectName}
              style={{ width: "30ch" }}
              onChange={(event) => {
                setProjectName(event.target.value);
                actionManager.executeAction(
                  actionChangeProjectName,
                  "ui",
                  event.target.value,
                );
              }}
            />
          )}
        </div>
      </div>
      <div className="ImageExportModal__settings">
        <h3>Exportar como</h3>

        <ExportSetting
          label={t("imageExportDialog.label.withBackground")}
          name="exportBackgroundSwitch"
        >
          <Switch
            name="exportBackgroundSwitch"
            checked={exportWithBackground}
            onChange={(checked) => {
              setExportWithBackground(checked);
              actionManager.executeAction(
                actionChangeExportBackground,
                "ui",
                checked,
              );
            }}
          />
        </ExportSetting>
        <ExportSetting label="Borda" name="exportBorderSwitch">
          <Switch
            name="exportBorderSwitch"
            checked={exportWithBorder}
            onChange={(checked) => {
              setExportWithBorder(checked);
            }}
          />
        </ExportSetting>
        <ExportSetting
          label={t("imageExportDialog.label.embedScene")}
          tooltip={t("imageExportDialog.tooltip.embedScene")}
          name="exportEmbedSwitch"
        >
          <Switch
            name="exportEmbedSwitch"
            checked={embedScene}
            onChange={(checked) => {
              setEmbedScene(checked);
              actionManager.executeAction(
                actionChangeExportEmbedScene,
                "ui",
                checked,
              );
            }}
          />
        </ExportSetting>
        <ExportSetting
          label={t("imageExportDialog.label.scale")}
          name="exportScale"
        >
          <RadioGroup
            name="exportScale"
            value={exportScale}
            onChange={(scale) => {
              setExportScale(scale);
              actionManager.executeAction(actionChangeExportScale, "ui", scale);
            }}
            choices={EXPORT_SCALES.map((scale) => ({
              value: scale,
              label: `${scale}\u00d7`,
            }))}
          />
        </ExportSetting>

        <div className="ImageExportModal__settings__buttons">
          <FilledButton
            className="ImageExportModal__settings__buttons__button"
            label={t("imageExportDialog.title.exportToPng")}
            onClick={async () => {
              onCloseRequest();
              setAppState({
                isExportingUI: true,
                exportOverlayVariant: "spinner",
              });
              await new Promise((resolve) => setTimeout(resolve, 50));
              try {
                if (selectedPapers.length > 1) {
                  const zipPromise = (async () => {
                    const zip = new JSZip();
                    for (let i = 0; i < selectedPapers.length; i++) {
                      const paper = selectedPapers[i];
                      const elementsForThisPaper = getExportedElementsForPaper(
                        paper.id,
                      );
                      const elementsMetadataForThisPaper = paperElements.filter(
                        (el) =>
                          elementsForThisPaper.some((e) => e.id === el.id),
                      );

                      const canvas = await exportToCanvas({
                        elements: elementsForThisPaper,
                        appState: {
                          ...appStateSnapshot,
                          exportBackground: exportWithBackground,
                          exportWithDarkMode,
                          exportScale,
                          exportEmbedScene: embedScene,
                        },
                        files,
                        exportPadding: DEFAULT_EXPORT_PADDING,
                        exportingFrame: paper as any,
                      });

                      // Respiro para não travar a UI no meio do processamento pesado
                      await new Promise((resolve) => setTimeout(resolve, 20));
                      let blob = await canvasToBlob(canvas);

                      if (embedScene) {
                        const { encodePngMetadata } = await import(
                          "../data/image"
                        );

                        await new Promise((resolve) => setTimeout(resolve, 20));
                        blob = await encodePngMetadata({
                          blob,
                          metadata: serializeAsJSON(
                            elementsMetadataForThisPaper,
                            appStateSnapshot,
                            files,
                            "local",
                          ),
                        });
                      }

                      const pad = (i + 1).toString().padStart(2, "0");
                      zip.file(`${projectName}_${pad}.png`, blob);
                    }
                    return await zip.generateAsync({ type: "blob" });
                  })();

                  await fileSave(zipPromise, {
                    name: projectName,
                    extension: "zip" as any,
                    description: "Exported PNGs ZIP",
                  });
                } else {
                  onExportImage(EXPORT_IMAGE_TYPES.png, exportedElements, {
                    ...exportImageOptions,
                    elementsForMetadata: paperElements,
                  });
                }
              } catch (error: any) {
                if (error.name !== "AbortError") {
                  console.error("Erro no PNG:", error);
                }
              } finally {
                setAppState({
                  isExportingUI: false,
                  exportOverlayVariant: "spinner",
                });
              }
            }}
            icon={downloadIcon}
          >
            {t("imageExportDialog.button.exportToPng")}
          </FilledButton>
          <FilledButton
            className="ImageExportModal__settings__buttons__button"
            label={t("imageExportDialog.title.exportToSvg")}
            onClick={async () => {
              onCloseRequest();
              setAppState({
                isExportingUI: true,
                exportOverlayVariant: "spinner",
              });
              await new Promise((resolve) => setTimeout(resolve, 50));
              try {
                if (selectedPapers.length > 1) {
                  const zipPromise = (async () => {
                    const zip = new JSZip();
                    for (let i = 0; i < selectedPapers.length; i++) {
                      const paper = selectedPapers[i];
                      const elementsForThisPaper = getExportedElementsForPaper(
                        paper.id,
                      );
                      const elementsMetadataForThisPaper = paperElements.filter(
                        (el) =>
                          elementsForThisPaper.some((e) => e.id === el.id),
                      );

                      const svg = await exportToSvg(
                        elementsForThisPaper,
                        {
                          exportBackground: exportWithBackground,
                          exportWithDarkMode,
                          viewBackgroundColor:
                            appStateSnapshot.viewBackgroundColor,
                          exportPadding: DEFAULT_EXPORT_PADDING,
                          exportScale,
                          exportEmbedScene: embedScene,
                        },
                        files,
                        {
                          exportingFrame: paper as any,
                          elementsForMetadata: elementsMetadataForThisPaper,
                        },
                      );

                      const svgString = SVG_DOCUMENT_PREAMBLE + svg.outerHTML;
                      const blob = new Blob([svgString], {
                        type: "image/svg+xml",
                      });

                      const pad = (i + 1).toString().padStart(2, "0");
                      zip.file(`${projectName}_${pad}.svg`, blob);
                    }
                    return await zip.generateAsync({ type: "blob" });
                  })();

                  await fileSave(zipPromise, {
                    name: projectName,
                    extension: "zip" as any,
                    description: "Exported SVGs ZIP",
                  });
                } else {
                  onExportImage(EXPORT_IMAGE_TYPES.svg, exportedElements, {
                    ...exportImageOptions,
                    elementsForMetadata: paperElements,
                  });
                }
              } catch (error: any) {
                if (error.name !== "AbortError") {
                  console.error("Erro no SVG:", error);
                }
              } finally {
                setAppState({
                  isExportingUI: false,
                  exportOverlayVariant: "spinner",
                });
              }
            }}
            icon={downloadIcon}
          >
            {t("imageExportDialog.button.exportToSvg")}
          </FilledButton>
          <FilledButton
            className="ImageExportModal__settings__buttons__button"
            label="Exportar para PDF"
            onClick={async () => {
              onCloseRequest();
              setAppState({
                isExportingUI: true,
                exportOverlayVariant: "dotmatrix",
              });
              await new Promise((resolve) => setTimeout(resolve, 50));
              try {
                const sceneJSON = embedScene
                  ? serializeAsJSON(
                      paperElements,
                      appStateSnapshot,
                      files,
                      "local",
                    )
                  : null;
                const appStateForPdf = {
                  ...appStateSnapshot,
                  viewBackgroundColor: exportWithBackground
                    ? appStateSnapshot.viewBackgroundColor
                    : "transparent",
                };

                const elementsForPdf = paperElements.map((el) => {
                  if (el.customData?.isPapel) {
                    return {
                      ...el,
                      backgroundColor: exportWithBackground
                        ? el.backgroundColor
                        : "transparent",
                      strokeColor: exportWithBorder
                        ? el.strokeColor
                        : "transparent",
                    };
                  }
                  return el;
                });

                await saveAsPdf({
                  data: {
                    elements: elementsForPdf,
                    appState: appStateForPdf as any,
                    files,
                  },
                  filename: projectName,
                  fileHandle: null,
                  embedSceneJSON: sceneJSON,
                });
              } catch (error: any) {
                if (error.name !== "AbortError") {
                  console.error("Erro no PDF:", error);
                  window.alert(
                    `Erro ao exportar PDF: ${
                      error.message || error.toString()
                    }`,
                  );
                }
              } finally {
                setAppState({
                  isExportingUI: false,
                  exportOverlayVariant: "spinner",
                });
              }
            }}
            icon={downloadIcon}
          >
            PDF
          </FilledButton>
          {(probablySupportsClipboardBlob || isFirefox) && (
            <FilledButton
              className="ImageExportModal__settings__buttons__button"
              label={t("imageExportDialog.title.copyPngToClipboard")}
              status={copyStatus}
              onClick={async () => {
                await onExportImage(
                  EXPORT_IMAGE_TYPES.clipboard,
                  exportedElements,
                  {
                    ...exportImageOptions,
                    elementsForMetadata: paperElements,
                  },
                );
                onCopy();
              }}
              icon={copyIcon}
            >
              {t("imageExportDialog.button.copyPngToClipboard")}
            </FilledButton>
          )}
        </div>
      </div>
    </div>
  );
};

type ExportSettingProps = {
  label: string;
  children: React.ReactNode;
  tooltip?: string;
  name?: string;
};

const ExportSetting = ({
  label,
  children,
  tooltip,
  name,
}: ExportSettingProps) => {
  return (
    <div className="ImageExportModal__settings__setting" title={label}>
      <label
        htmlFor={name}
        className="ImageExportModal__settings__setting__label"
      >
        {label}
        {tooltip && (
          <Tooltip label={tooltip} long={true}>
            {helpIcon}
          </Tooltip>
        )}
      </label>
      <div className="ImageExportModal__settings__setting__content">
        {children}
      </div>
    </div>
  );
};

export const ExportAsDialog = ({
  elements,
  appState,
  files,
  actionManager,
  onExportImage,
  onCloseRequest,
  setAppState,
  name,
}: {
  appState: UIAppState;
  elements: readonly NonDeletedExcalidrawElement[];
  files: BinaryFiles;
  actionManager: ActionManager;
  onExportImage: AppClassProperties["onExportImage"];
  onCloseRequest: () => void;
  setAppState: React.Component<any, AppState>["setState"];
  name: string;
}) => {
  // we need to take a snapshot so that the exported state can't be modified
  // while the dialog is open
  const [{ appStateSnapshot, elementsSnapshot }] = useState(() => {
    return {
      appStateSnapshot: cloneJSON(appState),
      elementsSnapshot: cloneJSON(elements),
    };
  });

  return (
    <Dialog onCloseRequest={onCloseRequest} size="wide" title={false}>
      <ExportAsModal
        elementsSnapshot={elementsSnapshot}
        appStateSnapshot={appStateSnapshot}
        files={files}
        actionManager={actionManager}
        onExportImage={onExportImage}
        name={name}
        exportWithDarkMode={appState.exportWithDarkMode}
        onCloseRequest={onCloseRequest}
        setAppState={setAppState}
      />
    </Dialog>
  );
};
